import type { BedenAnalizi, StokRow } from "@/api/stok";
import { DeepLink } from "@/components/DeepLink";
import type { ColumnDef } from "@/components/SortableTable";
import { Pill } from "@/components/ui";
import type { StokSeviyeKart } from "@/features/stok/seviye";
import { t, type TrKey } from "@/i18n";
import { fmtDate, fmtFloat, fmtInt, fmtMoney } from "@/lib/utils";

const MAX_SORT = Number.MAX_SAFE_INTEGER;

/** "Kalan gün" hücresi — 0 gün (tükendi) kırmızı, ≤7 gün sarı Pill; aksi halde düz metin. */
function kalanGunCell(v: number | null | undefined) {
  if (v == null) return "—";
  const rounded = Math.round(v);
  const label = `${rounded} ${t("stok.gun_kisaltma")}`;
  if (rounded <= 0) return <Pill tone="bad">{label}</Pill>;
  if (rounded <= 7) return <Pill tone="warn">{label}</Pill>;
  return label;
}

/** "tumu" sekmesi Grup sutunu — stok_analiz'in SQL CASE'inden gelen bucket adi (kategori alani). */
const GRUP_LABEL_KEY: Record<string, TrKey> = {
  tukendi: "stok.grup.tukendi",
  kritik: "stok.grup.kritik",
  yakin: "stok.grup.yakin",
  cok_satan: "stok.grup.cok_satan",
  az_satan: "stok.grup.az_satan",
  normal: "stok.grup.normal",
};

function grupLabel(kategori: string): string {
  const key = GRUP_LABEL_KEY[kategori];
  return key ? t(key) : kategori;
}

/**
 * Urun bazli satirlar (kritik/yakin/cok_satan/az_satan/tumu sekmeleri).
 * `grup: true` — yalniz "tumu" sekmesi: satirin hangi gruptan (tukendi/
 * kritik/yakin/cok_satan/normal/az_satan) geldigini gosteren ek sutun
 * (gorev sarti: "TUM gruplarini ... grup/kategori sutunuyla goster").
 */
export function getStokUrunColumns(opts: { grup?: boolean } = {}): ColumnDef<StokRow>[] {
  const columns: ColumnDef<StokRow>[] = [
    {
      key: "urun_adi",
      header: t("stok.col.urun"),
      accessor: (row) => (
        <div>
          <DeepLink href={row.admin_link} label={row.urun_adi || "—"} />
          {row.sku && <div className="font-mono text-[11px] text-muted">{row.sku}</div>}
          {row.stock_kind === 'bundle' && <span className="text-xs text-muted">Paket · bileşen stoğundan hesaplanır</span>}
        </div>
      ),
      sortValue: (row) => (row.urun_adi || "").toLowerCase(),
    },
    {
      key: "beden",
      header: t("stok.col.beden"),
      accessor: (row) => row.beden || "—",
      sortValue: (row) => row.beden || "",
    },
    {
      key: "satilan",
      header: t("stok.col.satilan"),
      accessor: (row) => fmtInt(row.satilan),
      sortValue: (row) => row.satilan,
      align: "right",
    },
    {
      key: "mevcut_stok",
      header: t("stok.col.stok"),
      accessor: (row) => fmtInt(row.mevcut_stok),
      sortValue: (row) => row.mevcut_stok,
      align: "right",
    },
    {
      key: "gunluk_hiz",
      header: t("stok.col.gunluk_hiz"),
      accessor: (row) => fmtFloat(row.gunluk_hiz),
      sortValue: (row) => row.gunluk_hiz ?? 0,
      align: "right",
    },
    {
      key: "kalan_gun",
      header: t("stok.col.kalan_gun"),
      accessor: (row) => kalanGunCell(row.kalan_gun),
      sortValue: (row) => (row.kalan_gun == null ? MAX_SORT : row.kalan_gun),
      align: "right",
    },
    {
      key: "son_siparis_tarihi",
      header: t("stok.col.son_siparis"),
      accessor: (row) => fmtDate(row.son_siparis_tarihi),
      sortValue: (row) => row.son_siparis_tarihi || "",
    },
  ];
  if (opts.grup) {
    columns.unshift({
      key: "kategori",
      header: t("stok.col.grup"),
      accessor: (row) => grupLabel(row.kategori),
      sortValue: (row) => grupLabel(row.kategori),
    });
  }
  return columns;
}

/** Beden analizi sekmesi — urun bazli degil, beden bazli toplam satirlar. */
export function getBedenColumns(): ColumnDef<BedenAnalizi>[] {
  return [
    {
      key: "beden",
      header: t("stok.col.beden"),
      accessor: (row) => row.beden,
      sortValue: (row) => row.beden,
    },
    {
      key: "toplam_satilan",
      header: t("stok.col.toplam_satilan"),
      accessor: (row) => fmtInt(row.toplam_satilan),
      sortValue: (row) => row.toplam_satilan,
      align: "right",
    },
    {
      key: "varyant_sayisi",
      header: t("stok.col.varyant_sayisi"),
      accessor: (row) => fmtInt(row.varyant_sayisi),
      sortValue: (row) => row.varyant_sayisi,
      align: "right",
    },
    {
      key: "mevcut_stok",
      header: t("stok.col.stok"),
      accessor: (row) => fmtInt(row.mevcut_stok),
      sortValue: (row) => row.mevcut_stok,
      align: "right",
    },
    {
      key: "stok_sifir",
      header: t("stok.col.stok_sifir"),
      accessor: (row) => fmtInt(row.stok_sifir),
      sortValue: (row) => row.stok_sifir,
      align: "right",
    },
    {
      key: "ort_kalan_gun",
      header: t("stok.col.kalan_gun"),
      accessor: (row) => kalanGunCell(row.ort_kalan_gun),
      sortValue: (row) => (row.ort_kalan_gun == null ? MAX_SORT : row.ort_kalan_gun),
      align: "right",
    },
  ];
}

/**
 * "Seviye" sekmesi beden rozeti — eski `legacy/js/stok.js` `bedenBadge()` ile
 * AYNI: etiket = beden (bosken SKU'nun son parcasi), stok=0 kirmizi, aksi
 * halde yesil; rozet uzerindeki title SKU + stok tasir. Etkilesimsiz (buton
 * degil) — 44px kurali gecerli degil.
 */
function seviyeBedenBadge(v: { sku?: string | null; beden?: string | null; stock: number }, key: string) {
  const stock = Number(v.stock) || 0;
  const label = (v.beden && v.beden.trim()) || (v.sku ? v.sku.split(/[-_]/).pop() || "?" : "?");
  const cls = stock === 0 ? "bg-red-pale text-red" : "bg-green-pale text-green";
  return (
    <span
      key={key}
      title={`${v.sku ?? ""} · Stok: ${stock}`}
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      {label} <span className="font-normal opacity-70">{stock}</span>
    </span>
  );
}

/**
 * "Seviye" sekmesi kart sutunlari — kaynak `kpis.kritik_stok.subitems.*.kartlar`
 * (bkz. features/stok/seviye.ts). Eski `legacy/js/stok.js` `kartSatir()` ile
 * ayni bilgiler: urun (deep-link) + SKU, kategori, fiyat, toplam stok, beden
 * rozetleri grubu.
 */
export function getSeviyeKartColumns(): ColumnDef<StokSeviyeKart>[] {
  return [
    {
      key: "name",
      header: t("stok.col.urun"),
      accessor: (row) => (
        <div>
          <DeepLink href={row.admin_link} label={row.name || "—"} />
          {row.sku && <div className="font-mono text-[11px] text-muted">{row.sku}</div>}
        </div>
      ),
      sortValue: (row) => (row.name || "").toLowerCase(),
    },
    {
      key: "category",
      header: t("stok.col.kategori"),
      accessor: (row) => row.category || "—",
      sortValue: (row) => row.category || "",
    },
    {
      key: "fiyat",
      header: t("stok.col.fiyat"),
      accessor: (row) => fmtMoney(row.fiyat),
      sortValue: (row) => row.fiyat ?? 0,
      align: "right",
    },
    {
      key: "total_stock",
      header: t("stok.col.toplam_stok"),
      accessor: (row) => fmtInt(row.total_stock),
      sortValue: (row) => row.total_stock,
      align: "right",
    },
    {
      key: "varyantlar",
      header: t("stok.col.varyantlar"),
      accessor: (row) =>
        row.varyantlar.length ? (
          <div className="flex max-w-[380px] flex-wrap gap-1">
            {row.varyantlar.map((v, i) => seviyeBedenBadge(v, `${row.product_card_id}-${v.sku ?? ""}-${v.beden ?? ""}-${i}`))}
          </div>
        ) : (
          seviyeBedenBadge({ stock: row.total_stock }, `${row.product_card_id}-toplam`)
        ),
    },
  ];
}
